import PropTypes from 'prop-types';
import { Trans, t } from '@lingui/macro';
import { Box, Tooltip, Typography, alpha, lighten, makeStyles, withStyles } from '@material-ui/core';
import { HelpOutline, PriorityHigh } from '@material-ui/icons';
import get from 'lodash/get';
import { isNil } from 'lodash';

const StyledTooltip = withStyles((theme) => ({
    tooltip: {
        maxWidth: 1000,
        padding: '8px',
        border: theme.palette.type === 'light' ? `1px solid ${theme.palette.text.primary}` : '',
        backgroundColor:
            theme.palette.type === 'light'
                ? theme.palette.background.paper
                : lighten(alpha(theme.palette.divider, 1), 0.85),
    },
}))(Tooltip);

const StyledHelpTooltip = withStyles(() => ({
    tooltip: {
        backgroundColor: '#6A6A6A',
    },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
    contentWrapper: {
        maxWidth: '240px',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',

        '& .MuiTypography-root': {
            color:
                theme.palette.type === 'light'
                    ? theme.palette.text.primary
                    : lighten(theme.palette.background.paper, 0.05),
        },

        '& .MuiTypography-body1': {
            fontSize: '14px',
            fontWeight: 800,
        },

        '& .MuiTypography-body2': {
            maxWidth: '240px',
            fontSize: '12px',
            fontWeight: 500,
        },

        '& .duplicateDisclaimer.MuiTypography-body2': {
            padding: '4px 0',
            fontWeight: 600,
            color: theme.palette.error.dark,

            '& .MuiSvgIcon-root': {
                color: theme.palette.error.dark,
            },
        },

        '& .fieldValueWrapper': {
            display: 'flex',
            gap: '2px',
            alignItems: 'center',

            '& .MuiSvgIcon-root': {
                fontSize: '16px',
                color:
                    theme.palette.type === 'light'
                        ? lighten(theme.palette.text.primary, 0.05)
                        : lighten(theme.palette.background.paper, 0.05),
            },
        },
    },
}));

// Array of objects that define what items to render in the hop tooltip. Objects should have a
// label that defines what label to render in the tooltip and a hopDataKey which defines where
// to get the data from the path hop data using dot notation.
const TOOLTIP_FIELDS = [
    {
        label: t`Avg Response Time`,
        hopDataKey: 'avgResponseTime',
        units: 'ms',
        formatter: (value) => (value < 1 ? '<1' : value),
        HelpTooltip: () => {
            return (
                <StyledHelpTooltip
                    placement="right"
                    title={
                        <Box sx={{ '& *': { fontSize: '12px' } }}>
                            <Typography>
                                <Trans>
                                    This value is the response time between this device and the nPoint that is running
                                    the test.
                                </Trans>
                            </Typography>
                        </Box>
                    }
                >
                    <HelpOutline />
                </StyledHelpTooltip>
            );
        },
    },
    {
        label: t`Hostname`,
        hopDataKey: 'hostname',
    },
    {
        label: t`ISP`,
        hopDataKey: 'location.provider',
    },
    {
        label: t`ASN`,
        hopDataKey: 'location.asn',
    },
    {
        label: t`City`,
        hopDataKey: 'location.city',
    },
    {
        label: t`Subdivison`,
        hopDataKey: 'location.subdivision',
    },
    {
        label: t`Country`,
        hopDataKey: 'location.country',
    },
];

function getTooltipContent(nodeData) {
    const { hopData } = nodeData;

    return (
        <>
            <Typography variant="body1">{hopData.ipAddress ? hopData.ipAddress : t`Unidentifiable Hop`}</Typography>
            {nodeData.isDuplicate && (
                <>
                    <Typography className="duplicateDisclaimer" variant="body2">
                        <PriorityHigh fontSize="inherit" />
                        {t`This device was found multiple times in the path, which could indicate a loop`}
                    </Typography>
                </>
            )}
            {!nodeData.isAgent &&
                (hopData.startHop && hopData.endHop ? (
                    <Typography variant="body2">
                        {t`Hop numbers`}: <strong>{hopData.startHop}</strong> - <strong>{hopData.endHop}</strong>
                    </Typography>
                ) : (
                    <Typography variant="body2">
                        {t`Hop number`}: <strong>{hopData.hopNumber}</strong>
                    </Typography>
                ))}
            {hopData.ipAddress &&
                TOOLTIP_FIELDS.map((field) => {
                    const fieldValue = get(hopData, field.hopDataKey);

                    if (!isNil(fieldValue)) {
                        const { label, formatter, units, HelpTooltip } = field;

                        return (
                            <div key={`${hopData.hopNumber}-${label}`} className="fieldValueWrapper">
                                <Typography variant="body2">
                                    {label}:{' '}
                                    <strong>
                                        {formatter ? formatter(fieldValue) : fieldValue}
                                        {units || ''}
                                    </strong>
                                </Typography>
                                {HelpTooltip && <HelpTooltip />}
                            </div>
                        );
                    }

                    return null;
                })}
            {!hopData.ipAddress && hopData.statusMessage && (
                <Typography variant="body2">
                    {t`Status Message`}: <strong>{hopData.statusMessage}</strong>
                </Typography>
            )}
        </>
    );
}

export function HopNodeTooltip({ nodeData, children }) {
    const styles = useStyles();

    return (
        <StyledTooltip interactive title={<Box className={styles.contentWrapper}>{getTooltipContent(nodeData)}</Box>}>
            {children}
        </StyledTooltip>
    );
}

HopNodeTooltip.propTypes = {
    nodeData: PropTypes.object.isRequired,
};
